import type { BenchmarkData } from '@app/src/pages/SustainabilityProfile/benchmark/types'
import type { BenchmarkGroupType } from '@app/src/types/benchmark'
import { Stack } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'
import {
  disclosureBadge,
  disclosureBar,
  disclosureComparison,
  integrityBar,
  integrityComparison,
  KpiMetric,
  resolveKpiCardState,
} from '../benchmark/kpiCardLogic'
import BenchmarkKpiCard from './BenchmarkKpiCard'
import { INTEGRITY_SECTION_ID } from './IntegrityScreeningSection'

interface ProfileKpiCardsProps {
  overallScore?: number
  findingsTotal?: number
  benchmark?: BenchmarkData
  peerGroup: BenchmarkGroupType
  isScoreLoading: boolean
  isFindingsLoading: boolean
}

const scrollToIntegritySection = () =>
  document.getElementById(INTEGRITY_SECTION_ID)?.scrollIntoView({ behavior: 'smooth', block: 'start' })

const ProfileKpiCards: React.FC<ProfileKpiCardsProps> = ({
  overallScore,
  findingsTotal,
  benchmark,
  peerGroup,
  isScoreLoading,
  isFindingsLoading,
}) => {
  const { formatMessage } = useIntl()

  const disclosure = resolveKpiCardState(overallScore, disclosureComparison(benchmark?.overall))
  const integrity = resolveKpiCardState(findingsTotal, integrityComparison(findingsTotal, benchmark?.integrity))

  return (
    <Stack direction="row" spacing={2}>
      <BenchmarkKpiCard
        metric={KpiMetric.DisclosureScore}
        state={disclosure}
        unit={formatMessage({ id: 'sustainabilityProfile.kpi.disclosureScore.unit' })}
        badge={disclosureBadge(disclosure)}
        bar={disclosureBar(disclosure)}
        isLoading={isScoreLoading}
        peerGroup={peerGroup}
      />
      <BenchmarkKpiCard
        metric={KpiMetric.Integrity}
        state={integrity}
        unit={` ${formatMessage({ id: 'sustainabilityProfile.kpi.integrity.unit' }, { count: findingsTotal ?? 0 })}`}
        bar={integrityBar(integrity, benchmark?.integrity)}
        isLoading={isFindingsLoading}
        peerGroup={peerGroup}
        onClick={findingsTotal ? scrollToIntegritySection : undefined}
      />
    </Stack>
  )
}

export default ProfileKpiCards
